import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (totalPages <= 1) return null;

  return (
    <nav className="flex justify-center" aria-label="Seitennavigation">
      <div className="inline-flex items-center gap-1">
        {/* Previous Button */}
        <button 
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
          className={cn(
            "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200",
            currentPage === 1 
              ? "text-gray-300 cursor-default" 
              : "text-gray-700 hover:bg-gray-100 active:scale-95"
          )}
          aria-label="Vorherige Seite"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Zurück</span>
        </button>
        
        {/* Desktop: Page Numbers */}
        <div className="hidden md:flex items-center gap-0.5">
          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <button
                key={index}
                onClick={() => onPageChange(page)}
                className={cn(
                  "w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009544]/30",
                  currentPage === page
                    ? "bg-[#009544] text-white shadow-sm"
                    : "text-gray-700 hover:bg-gray-100"
                )}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            ) : (
              <span key={index} className="w-8 flex items-center justify-center text-gray-400 text-sm select-none">
                ···
              </span>
            )
          ))}
        </div>
        
        {/* Mobile: Current page display + selector */}
        <div className="md:hidden flex items-center gap-2 px-3">
          <span className="text-sm text-gray-600 font-medium tabular-nums">
            {currentPage} / {totalPages}
          </span>
          {totalPages > 3 && (
            <div className="relative">
              <select
                value={currentPage}
                onChange={(e) => onPageChange(parseInt(e.target.value))}
                className="pl-3 pr-7 py-1.5 min-h-[36px] border border-gray-200 rounded-lg text-sm bg-white appearance-none focus-visible:ring-2 focus-visible:ring-[#009544]/40 focus-visible:border-[#009544]"
                aria-label="Seite auswählen"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <option key={page} value={page}>
                    Seite {page}
                  </option>
                ))}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 rotate-90" />
            </div>
          )}
        </div>
        
        {/* Next Button */}
        <button 
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={cn(
            "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200",
            currentPage === totalPages 
              ? "text-gray-300 cursor-default" 
              : "text-gray-700 hover:bg-gray-100 active:scale-95"
          )}
          aria-label="Nächste Seite"
        >
          <span className="hidden sm:inline">Weiter</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
};
